import { useState, useEffect } from "react";
import { FiX, FiCode, FiMail, FiSend } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { sendTestEmail } from "../api/contacts";
import toast from "react-hot-toast";

// Helper function to replace placeholders with sample data
function previewWithSampleData(text) {
  const sampleData = {
    name: "John Smith",
    company_name: "Oris Dental Center",
    email: "odental657@gmail.com",
    phone: "043809990",
    category: "Dentists",
    area: "Jumeirah",
    address: "Umm Sequeim 3, Jumeirah Road, Opposite Burj Al Arab",
    website: "https://www.orisdentalcenter.ae/",
    services_offered: "Dental Care, Cosmetic Dentistry, Orthodontics",
  };

  let result = text;
  Object.keys(sampleData).forEach((key) => {
    const regex = new RegExp(`\\{${key}\\}`, "g");
    result = result.replace(regex, sampleData[key]);
  });

  return result;
}

export default function TemplatePreviewModal({ template, onClose }) {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState("raw"); // 'raw' or 'preview'
  const [savedEmails, setSavedEmails] = useState([]);
  const [selectedTestEmail, setSelectedTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("saved_test_emails");
    let emails = [user?.email].filter(Boolean);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          emails = parsed;
        }
      } catch (e) {}
    }
    setSavedEmails(emails);
    if (emails.length > 0) setSelectedTestEmail(emails[0]);
  }, [user]);

  const handleSendTestEmail = async () => {
    if (!selectedTestEmail) {
      toast.error("Please select or enter a test email address!");
      return;
    }
    setSendingTest(true);
    try {
      const res = await sendTestEmail(token.access, selectedTestEmail, template.id);
      toast.success(res.message || `Test email sent to ${selectedTestEmail}! 🎉`);
    } catch (err) {
      toast.error(err.message || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  if (!template) return null;


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{template.name}</h2>
            <p className="text-sm text-gray-500 mt-1">
              Created on {new Date(template.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-white">
          <div className="flex px-6">
            <button
              onClick={() => setActiveTab("raw")}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "raw"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              <FiCode size={16} />
              Raw Template
            </button>
            <button
              onClick={() => setActiveTab("preview")}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "preview"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              <FiMail size={16} />
              Email Preview
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === "raw" ? (
            <div className="space-y-4">
              {/* Subject */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Subject Line
                </label>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <code className="text-sm text-gray-800 font-mono break-words">
                    {template.subject}
                  </code>
                </div>
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Body
                </label>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <pre className="text-sm text-gray-800 font-mono whitespace-pre-wrap break-words">
                    {template.body}
                  </pre>
                </div>
              </div>

              {/* Available Placeholders */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">Available Placeholders</h4>
                <div className="flex flex-wrap gap-2">
                  {['{name}', '{company_name}', '{email}', '{phone}', '{category}', '{area}', '{address}', '{website}', '{services_offered}'].map((placeholder) => (
                    <span
                      key={placeholder}
                      className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-mono rounded"
                    >
                      {placeholder}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Preview Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> This preview uses sample contact data to show how the email will look to recipients.
                </p>
              </div>

              {/* Email Preview Card */}
              <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
                {/* Email Header */}
                <div className="bg-gray-100 px-4 py-3 border-b border-gray-300">
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                    <span className="font-semibold">To:</span>
                    <span>odental657@gmail.com</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <span className="text-gray-600 font-normal">Subject:</span>
                    <span>{previewWithSampleData(template.subject)}</span>
                  </div>
                </div>

                {/* Email Body */}
                <div className="p-6 bg-white">
                  <div className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {previewWithSampleData(template.body)}
                  </div>
                </div>
              </div>

              {/* Sample Data Used */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Sample Data Used</h4>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-gray-500">Name:</span>
                    <span className="ml-2 text-gray-800 font-medium">John Smith</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Company:</span>
                    <span className="ml-2 text-gray-800 font-medium">Oris Dental Center</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Category:</span>
                    <span className="ml-2 text-gray-800 font-medium">Dentists</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Area:</span>
                    <span className="ml-2 text-gray-800 font-medium">Jumeirah</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Website:</span>
                    <span className="ml-2 text-gray-800 font-medium">https://www.orisdentalcenter.ae/</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer with Test Email Option */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 w-full md:w-auto">
            <select
              value={selectedTestEmail}
              onChange={(e) => setSelectedTestEmail(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              {savedEmails.map((email, idx) => (
                <option key={idx} value={email}>
                  {email}
                </option>
              ))}
            </select>

            <button
              onClick={handleSendTestEmail}
              disabled={sendingTest}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-lg flex items-center gap-2 transition disabled:opacity-50 shrink-0"
            >
              <FiSend size={15} />
              {sendingTest ? "Sending..." : "Send Test Email"}
            </button>
          </div>

          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium text-sm w-full md:w-auto"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}